/**
 * API 엔드포인트 함수 모음
 *
 * 모든 함수는 client.ts의 get/post/patch를 사용
 * 16개 엔드포인트를 여기서 관리:
 * - 1개 인증 (login)
 * - 4개 대시보드 (KPI, 편성표, 히트맵, 모듈 헬스)
 * - 3개 모듈 (목록, 지금 수집, 업데이트)
 * - 4개 Job (목록, 상세, 스냅샷 URL, 재시도)
 * - 4개 데이터 브라우저 (모듈 목록, 스키마, 행, 연관 행)
 */

import { get, post, patch, getBlob } from './client';
import type {
  LoginResponse,
  DashboardKpiResponse,
  ScheduleResponse,
  HeatmapResponse,
  ModuleHealthResponse,
  ModuleListResponse,
  RunNowResponse,
  JobListResponse,
  JobDetailDto,
  SnapshotUrlResponse,
  ModuleSnapshots,
  SubmitResponse,
  RowsResponse,
  RelatedRowsResponse,
  GroupByResult,
  JobStagesResponse,
  ModulePatch,
  JobFilters,
} from './types';

// ============================================================================
// 인증 (Authentication)
// ============================================================================

/**
 * 로그인: 관리자 ID/비밀번호로 토큰 발급
 * 이 함수만 토큰 없이 전송 (useToken=false)
 */
export function login(id: string, password: string): Promise<LoginResponse> {
  return post<LoginResponse>('/auth/login', { id, password }, false);
}

// ============================================================================
// 대시보드 (Dashboard)
// ============================================================================

/**
 * KPI 요약: 모듈 수, 활성 상태, 최근 24시간 실패 등
 * DashboardScreen의 PipelineKpiRow에서 사용
 */
export function fetchDashboardKpi(): Promise<DashboardKpiResponse> {
  return get<DashboardKpiResponse>('/dashboard/kpi-summary');
}

/**
 * 오늘 편성표: 크론 전개 + Job 매칭 결과
 * @param date ISO 날짜 (생략하면 오늘)
 * DashboardScreen의 DailyScheduleBoard에서 사용
 */
export function fetchSchedule(date?: string): Promise<ScheduleResponse> {
  const query = date ? `?date=${encodeURIComponent(date)}` : '';
  return get<ScheduleResponse>(`/dashboard/schedule${query}`);
}

/**
 * 히트맵: 최근 N일간 모듈별 실행 상태
 * @param days 조회 기간 (기본값 1)
 * DashboardScreen의 RunHeatmap에서 사용
 */
export function fetchHeatmap(days = 2): Promise<HeatmapResponse> {
  return get<HeatmapResponse>(`/dashboard/heatmap?days=${days}`);
}

/**
 * 모듈 헬스: 파이프라인 상태 (필터링 가능)
 * @param filters 필터 옵션 (problemsOnly, search)
 * @param limit 페이지 크기 (기본값 50)
 * @param offset 시작 위치 (기본값 0)
 * DashboardScreen의 ModuleHealthTable에서 사용
 */
export interface ModuleHealthFilters {
  problemsOnly?: boolean; // true면 bad=true 항목만
  search?: string; // 모듈명 검색
}

export function fetchModuleHealth(
  filters?: ModuleHealthFilters,
  limit = 50,
  offset = 0
): Promise<ModuleHealthResponse> {
  const params = new URLSearchParams({
    problemsOnly: String(filters?.problemsOnly ?? false),
    search: filters?.search ?? '',
    limit: String(limit),
    offset: String(offset),
  });
  return get<ModuleHealthResponse>(`/dashboard/module-health?${params}`);
}

// ============================================================================
// 모듈 (Modules)
// ============================================================================

/**
 * 모듈 목록 조회
 * ModulesScreen에서 모듈 카드를 나열할 때 사용
 */
export function fetchModules(): Promise<ModuleListResponse> {
  return get<ModuleListResponse>('/modules');
}

/**
 * 지금 수집: 모듈을 즉시 실행하고 Job 생성
 * @param name 모듈명 (naverdiscuss, reddit 등)
 * ModuleConfigCard의 "▶ 지금 수집" 버튼에서 호출
 */
export function runNow(name: string): Promise<RunNowResponse> {
  return post<RunNowResponse>(`/modules/${encodeURIComponent(name)}/run-now`);
}

/**
 * 즉시 수집(ingest): 모듈 전용 엔드포인트로 수집기를 바로 실행하고 생성된 Job을 반환.
 * run-now(스케줄러 트리거)와 별개 경로. body를 생략하면 수집기 기본 input을 사용.
 * @param key ingest 경로 키 (예: naverdiscuss, reddit, stckdcby, etfpban)
 * @param body 수집 옵션 (예: brightdata 수집기는 { local_scrap: false }). 생략 가능.
 * ModuleConfigCard의 "▶ 지금 수집" 버튼에서 호출.
 */
export function ingest(
  key: string,
  body?: object
): Promise<RunNowResponse> {
  return post<RunNowResponse>(`/${encodeURIComponent(key)}/ingest`, body);
}

/**
 * 모듈 설정 업데이트: 부분 업데이트 (PATCH)
 * @param name 모듈명
 * @param updates 변경할 필드만 포함 (collectorType, enabled, cronExpr 등)
 * ModuleConfigCard의 수정 모달에서 호출
 */
export function updateModule(
  name: string,
  updates: ModulePatch
): Promise<ModuleListResponse> {
  return patch<ModuleListResponse>(
    `/modules/${encodeURIComponent(name)}`,
    updates
  );
}

// ============================================================================
// 수집 이력 (Jobs)
// ============================================================================

/**
 * Job 목록 조회 (필터링 + 페이지네이션)
 * @param filters 필터 (module, status, trigger, date)
 * @param limit 페이지 크기 (기본값 50)
 * @param offset 시작 위치 (기본값 0)
 * JobsScreen의 JobTable에서 사용
 */
export function fetchJobs(
  filters?: JobFilters,
  limit = 50,
  offset = 0
): Promise<JobListResponse> {
  const params = new URLSearchParams({
    module: filters?.module ?? '',
    status: filters?.status ?? '',
    trigger: filters?.trigger ?? '',
    date: filters?.date ?? '',
    limit: String(limit),
    offset: String(offset),
  });
  return get<JobListResponse>(`/jobs?${params}`);
}

/**
 * Job 상세 조회
 * @param id Job ID
 * JobDetailScreen에서 선택된 Job의 상세 정보를 표시할 때 사용
 */
export function fetchJobDetail(id: number): Promise<JobDetailDto> {
  return get<JobDetailDto>(`/jobs/${id}`);
}

/**
 * 원본 스냅샷 URL 조회
 * @param id Job ID
 * JobDetailScreen의 "원본 스냅샷" 버튼에서 URL을 새 탭으로 열 때 사용
 */
export function snapshotUrl(id: number): Promise<SnapshotUrlResponse> {
  return get<SnapshotUrlResponse>(`/jobs/${id}/snapshot-url`);
}

/**
 * 모듈 스냅샷 목록 조회 (GET /api/{module}/snapshots)
 * 각 스냅샷의 id는 Job의 collectionId와 동일. 원본 스냅샷 존재 확인에 사용.
 */
export function fetchModuleSnapshots(module: string): Promise<ModuleSnapshots> {
  return get<ModuleSnapshots>(`/${module}/snapshots`);
}

/**
 * 스냅샷 파일 다운로드 (GET /api/{module}/snapshots/{id})
 * @param module 모듈명
 * @param id 스냅샷 id (= Job의 collectionId)
 * 바이너리 응답을 Blob으로 받고 Content-Disposition 파일명을 함께 반환.
 */
export function downloadSnapshot(
  module: string,
  id: string
): Promise<{ blob: Blob; filename: string }> {
  return getBlob(`/${module}/snapshots/${id}`, `${id}.json`);
}

/**
 * 문서 파일(md/이미지) 다운로드 (GET /api/{module}/doc-file?path={relpath})
 *
 * docparser 수집 모듈(comp_earnings 등)이 남긴 변환 산출물을 인증 하에 내려받는다.
 * 스냅샷 row의 md_path / image_paths[] 값을 path에 그대로 넘긴다.
 * (경로는 이미 `<module>/<batchId>/...` 형태 — 백엔드가 데이터 루트 기준으로 resolve)
 *
 * @param module 모듈명 (예: comp_earnings)
 * @param path   스냅샷에 기록된 배치 기준 상대 경로
 *
 * 주의: 토큰이 필요해 <img src>로 직접 못 건다. Blob으로 받아 objectURL로 변환해 사용.
 * (useDocViewer 참고)
 */
export function downloadDocFile(
  module: string,
  path: string
): Promise<{ blob: Blob; filename: string }> {
  const fallback = path.split('/').pop() || 'file';
  return getBlob(`/${module}/doc-file?path=${encodeURIComponent(path)}`, fallback);
}

/**
 * Job 진행 단계 조회 (GET /jobs/{id}/stages)
 * @param id Job ID
 * 상위 5단계 outline(항상 5칸) + 진행 로그 반환.
 * StageView(수집 이력 / Job 상세)에서 스테이지 매트릭스를 채울 때 사용.
 */
export function fetchJobStages(id: number): Promise<JobStagesResponse> {
  return get<JobStagesResponse>(`/jobs/${id}/stages`);
}

/**
 * Job 재시도: 기존 Job의 inputJson을 재사용해 새 Job 생성
 * @param id 원본 Job ID
 * JobDetailScreen의 "같은 입력으로 재시도" 버튼에서 호출
 */
export function retryJob(id: number): Promise<SubmitResponse> {
  return post<SubmitResponse>(`/jobs/${id}/retry`);
}

// ============================================================================
// 데이터 브라우저 (Data Browser)
// ============================================================================


/**
 * 테이블 행 조회 (필터링 + 페이지네이션)
 * @param module 모듈명
 * @param table 테이블명
 * @param filters 필터 (search)
 * @param limit 페이지 크기 (기본값 50)
 * @param offset 시작 위치 (기본값 0)
 * DataBrowserScreen의 DataGrid 렌더링에서 사용
 */
export interface RowsFilters {
  search?: string; // 전체 컬럼 텍스트 검색
}

export function fetchTableRows(
  module: string,
  table: string,
  filters?: RowsFilters,
  limit = 50,
  offset = 0
): Promise<RowsResponse> {
  const params = new URLSearchParams({
    search: filters?.search ?? '',
    limit: String(limit),
    offset: String(offset),
  });
  return get<RowsResponse>(
    `/data/${encodeURIComponent(module)}/${encodeURIComponent(table)}/rows?${params}`
  );
}

/**
 * 연관 행 조회 (Foreign Key 관계)
 * @param module 모듈명
 * @param table 현재 테이블명
 * @param rowId 선택 행의 첫 컬럼 값 (예: nid=101928)
 * @param foreignTable 연관 테이블명 (예: comments)
 * RowDetailPanel의 "comments 34건 보기 →" 버튼에서 호출
 */
export function fetchRelatedRows(
  module: string,
  table: string,
  rowId: string,
  foreignTable: string
): Promise<RelatedRowsResponse> {
  return get<RelatedRowsResponse>(
    `/data/${encodeURIComponent(module)}/${encodeURIComponent(table)}/${encodeURIComponent(rowId)}/related/${encodeURIComponent(foreignTable)}`
  );
}

/**
 * 그룹 집계 조회 (GET /data/{schema}/{table}/groupby)
 * @param cols 그룹 컬럼 3단계 (pri/sec/ter). 빈 값은 미전송.
 *   - pri 미지정 시 서버 기본 primary_group(날짜 절삭)으로 집계.
 *   - primary_group 미설정 테이블(예: comments)은 pri 필수.
 * @param limit 페이지 크기 1~1000 (기본 50)
 * 집계 뷰어(PopulationContainer) 차트·표·필터 패널에서 사용.
 */
export function fetchGroupBy(
  module: string,
  table: string,
  cols: { pri?: string; sec?: string; ter?: string },
  limit = 50,
  offset = 0,
  // 기간 필터(yyyy-MM-dd). 테이블 기본 날짜 컬럼 기준, to 는 그날 포함.
  // 기본 날짜 컬럼이 없는 테이블에 지정하면 서버가 400을 던지므로 호출부에서 가드.
  range?: { from?: string; to?: string }
): Promise<GroupByResult> {
  const params = new URLSearchParams();
  if (cols.pri) params.set('gcol_pri', cols.pri);
  if (cols.sec) params.set('gcol_sec', cols.sec);
  if (cols.ter) params.set('gcol_ter', cols.ter);
  if (range?.from) params.set('from', range.from);
  if (range?.to) params.set('to', range.to);
  params.set('limit', String(limit));
  params.set('offset', String(offset));
  return get<GroupByResult>(
    `/data/${encodeURIComponent(module)}/${encodeURIComponent(table)}/groupby?${params}`
  );
}
